'use client'
import { Campaign as CampaignObject, MediaBlock, RichText } from '@/payload-types'
import { Blog as BlogObject } from '@/payload-types'
import { Jumbotron } from '@/components/jumbotron/jumbotron'
import { AccordionPost } from '@/components/accordions/accordionPost'
import { RichTextComponent } from '../rich-text/richText'
import { Quote } from '@/components/quote/quote'
import { MediaComponent } from '@/components/media/media'
import AnchorLink from '../anchor-link/anchorLink'
import { CreateAnchorLink, checkIfHaveKeyProfiles } from '@/lib/postFunctions'
import ProfileList from '../profile-list/profileList'
import ShareLinkIcons from '@/components/share-link-icons/shareLinkIcons'
import EmbeddedGalleryComponent from '../galleries/embedded-gallery/embeddedGallery'
import { CANARY_MISSION } from '@/enum/enum'
import NewsletterSignupAndDonate from '../newsletter-signup-and-donate/newsletterSignupAndDonate'
import { EmbeddedVideo } from '@/components/embedded-video/embeddedVideo'
import { PdfDownloadSubmission } from '../pdfDownloadSubmission/pdfDownloadSubmission'
import RegularProfileList from '../hasManyProfileList/regularProfileList'
import CompactProfileList from '../hasManyProfileList/compactProfileList'
import KeyProfileList from '../hasManyProfileList/keyProfileList'
import { TextSection } from '../textSection/textSection'
import { useState } from 'react'
import { PopupComponent } from '../popup/popup'

interface IProps {
  postObject?: CampaignObject | BlogObject | null
  postType?: 'campaign' | 'blog' | null
}

interface AnchorLinkProps {
  label: string
  href: string
}

export const PostComponent = ({ postObject, postType }: IProps) => {
  const jumbotron = postObject?.block?.find((item) => item.blockType == 'jumbotron')
  const jumbotronIndex = postObject?.block?.findIndex((item) => item.blockType == 'jumbotron')
  const richText = postObject?.block?.filter((item) => item.blockType == 'RichText' || item.blockType == 'textSection')
  const arrayOfLinks: AnchorLinkProps[] = []
  const [openMediaPopup, setOpenMediaPopup] = useState(false)
  const [openMediaId, setOpenMediaId] = useState<string | null>(null)
  richText?.map((item) => {
    if (item.blockType === 'RichText') {
      const newAnchorLink = CreateAnchorLink(item)
      if (newAnchorLink && !arrayOfLinks.some((link) => link.label === newAnchorLink.label)) {
        arrayOfLinks.push(newAnchorLink)
      }
    }
    else if (item.blockType === 'textSection') {
      item.content?.map((item) => {
        if (typeof item === 'object' && item.richText) {
          const pretendRichTextBlock = {
            richText: item?.richText,
            blockType: 'RichText',
          } as RichText
          const newAnchorLink = CreateAnchorLink(pretendRichTextBlock)
          if (newAnchorLink && !arrayOfLinks.some((link) => link.label === newAnchorLink.label)) {
            arrayOfLinks.push(newAnchorLink)
          }
        }
      })
    }
  })

  const postUrl = `${CANARY_MISSION.BASIC_URL}/${postType}/${postObject?.slug}`
  const postTitle = `${CANARY_MISSION.NAME} ${postType} - ${postObject?.title}`
  const campaignTitle = `REPORT: ${postObject ? postObject?.title : ''}`
  const ifKeyProfiles = postObject ? checkIfHaveKeyProfiles(postObject) : false

  return (
    <>
      {jumbotron && jumbotron.blockType == 'jumbotron' && (
        <Jumbotron
          jumbotronData={jumbotron}
          publish={postObject?.firstPublishedAt || postObject?.publishDate || ''}
        />
      )}
      <div className="PageInternalContainer flex flex-col md:flex-row min-h-screen items-start">
        <div className="w-full mb-[13px] md:mb-[0px] md:sticky md:w-[255px] md:ml-[10px] md:top-[71px] xl:top-[15px]">
          <AnchorLink arrayOfLinks={arrayOfLinks} title="Contents" />
          <ShareLinkIcons
            urlToShare={postUrl}
            pageTitle={postTitle}
            campaignTitle={campaignTitle}
          />
        </div>
        <div className="max-w-full md:w-[650px] md:border-l xl:border-r border-gray-200 md:pl-5 md:pr-5">
          {postObject?.block?.map(
            (item, index) =>
              (item.blockType !== 'jumbotron' || index !== jumbotronIndex) && (
                <div className={index === 1 ? '' : 'my-[20px]'} key={index}>
                  {(() => {
                    switch (item.blockType) {
                      case 'accordion':
                        return <AccordionPost accordionData={item} key={index} />
                      case 'jumbotron':
                        return <Jumbotron jumbotronData={item} key={index} rounded={true} />
                      case 'RichText':
                        return <RichTextComponent richTextData={item} key={index} />
                      case 'quotes':
                        return <Quote quoteData={item} key={index} />
                      case 'mediaBlock':
                        return typeof item?.media === 'object' && item?.media?.mimeType?.startsWith('video/') || item?.link
                          ? <MediaComponent mediaData={item} key={index} />
                          : <>
                            {openMediaPopup && openMediaId === item.id && (
                              <PopupComponent onClose={() => setOpenMediaPopup(false)}>
                                <MediaComponent mediaData={item} key={index} showCaption={false} />
                              </PopupComponent>
                            )}<div onClick={() => { setOpenMediaPopup(true); setOpenMediaId(item.id || null) }} className="cursor-pointer">
                              <MediaComponent mediaData={item} key={index} />
                            </div>
                          </>
                      case 'carousel':
                        return <EmbeddedGalleryComponent mediaBlocks={item.element as MediaBlock[]} key={index} />
                      case 'profileList':
                        return <ProfileList profileListData={item} key={index} />
                      case 'embeddedvideo':
                        return <EmbeddedVideo embeddedVideoData={item} key={index} />
                      case 'pdfDownload':
                        return <PdfDownloadSubmission pdfDownloadData={item} key={index} />
                      case 'regularProfileList':
                        return <RegularProfileList profileListData={item} key={index} />
                      case 'compactProfileList':
                        return <CompactProfileList profileListData={item} key={index} />
                      case 'textSection':
                        return <TextSection textSectionData={item} key={index} />
                      default:
                        return null
                    }
                  })()}
                </div>
              ),
          )}
        </div>
        <div className="w-[255px] pl-[27px] hidden xl:flex xl:flex-col xl:self-stretch">
          {ifKeyProfiles && (
            <>
              <p className="text-xl font-bold pb-1 divider mb-4">Key Profiles</p>
              {postObject?.block?.map((item, index) => (
                <div key={index}>
                  {(() => {
                    switch (item.blockType) {
                      case 'profileList':
                        return (
                          <ProfileList profileListData={item} featuredSection={true} key={index} />
                        )
                      case 'keyProfileList':
                        return <KeyProfileList profileListData={item} key={index} />
                      default:
                        return null
                    }
                  })()}
                </div>
              ))}
            </>
          )}
          <div className="md:sticky top-0 md:top-[71px] xl:top-[15px]">
            <NewsletterSignupAndDonate />
          </div>
        </div>
      </div>
    </>
  )
}

export default PostComponent
